/**
 * Generadores de datos de gráficos (Mocks reutilizables)
 * Funciones puras, sin dependencias de componentes
 */

import { format, addDays, startOfWeek } from "date-fns";
import { es } from "date-fns/locale";
import { safeDate, type DateInput } from "./formatters";

/** Capitaliza la primera letra de una cadena */
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** Fecha en formato YYYY-MM-DD (local) — para deep-links de detalle */
const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Rango inclusivo (desde–hasta) que representa un punto para deep-links de detalle */
const pointRange = (
  desde: Date,
  hasta: Date,
): Pick<ChartDataPoint, "periodDesde" | "periodHasta"> => ({
  periodDesde: isoDay(desde),
  periodHasta: isoDay(hasta),
});

export interface ChartDataPoint {
  name: string;
  shortName?: string;
  fullName?: string;
  fullDate: Date;
  /** Ventas de caja (POS físico) — eje Y principal */
  total: number;
  /** Ventas de tienda virtual (canal web/whatsapp) — serie opcional */
  tienda?: number;
  secondary?: number;
  type: "hour" | "day" | "week" | "month";
  weekIndex?: number;
  /** Inicio del periodo representado (YYYY-MM-DD, inclusivo) — deep-links de detalle */
  periodDesde?: string;
  /** Fin del periodo representado (YYYY-MM-DD, inclusivo) */
  periodHasta?: string;
  /** Etiqueta del deep-link (chip del buscador), distinta del `fullName` del tooltip. */
  drillLabel?: string;
}

/** Las 24 horas del día (00:00 – 23:00) — negocios con operación 24h. */
const ALL_DAY_HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Multiplicador de demanda según la hora: madrugada baja, almuerzo/cena pico. */
const demandFactorByHour = (hour: number): number => {
  if (hour <= 5) return 0.2; // madrugada: actividad mínima
  if (hour <= 7) return 0.5; // apertura temprana
  if (hour >= 12 && hour <= 13) return 3; // almuerzo
  if (hour >= 18 && hour <= 20) return 2.6; // cena
  if (hour >= 22) return 0.7; // noche tardía
  return 1;
};

/**
 * Genera datos por hora para un día específico (las 24 horas del día).
 */
export const generateHourlyData = (dateInput: DateInput): ChartDataPoint[] => {
  const date = safeDate(dateInput);

  return ALL_DAY_HOURS.map((hour) => {
    const factor = demandFactorByHour(hour);
    const value = Math.round(45000 * factor + Math.random() * 30000 * factor);

    const hourDate = new Date(date);
    hourDate.setHours(hour, 0, 0, 0);

    return {
      name: format(hourDate, "h a", { locale: es })
        .replace("a. m.", "am")
        .replace("p. m.", "pm"),
      fullDate: hourDate,
      total: value,
      type: "hour",
      ...pointRange(date, date),
    };
  });
};

/**
 * Genera datos diarios para una semana
 */
export const generateDailyData = (
  startDateInput: DateInput,
): ChartDataPoint[] => {
  const startDate = safeDate(startDateInput);
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });

  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i);
    const short = format(d, "EEE", { locale: es });
    return {
      name: format(d, "EEEE", { locale: es }),
      // Vista semanal unificada: el `shortName` es solo el día ("lun", "mar"…),
      // sin el número de fecha, igual que en la Evolución de métodos de pago.
      shortName: short,
      fullName: `${cap(format(d, "EEEE", { locale: es }))} ${d.getDate()}`,
      fullDate: d,
      total: 800000 + Math.floor(Math.random() * 400000),
      type: "day",
      ...pointRange(d, d),
    };
  });
};

/**
 * Genera datos diarios para todos los días de un mes
 */
export const generateDailyDataForMonth = (
  monthDateInput: DateInput,
): ChartDataPoint[] => {
  const monthDate = safeDate(monthDateInput);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month, i + 1);
    return {
      name: String(i + 1),
      shortName: String(i + 1),
      fullName: `${d.getDate()} de ${cap(format(d, "MMMM", { locale: es }))}`,
      fullDate: d,
      total: 800000 + Math.floor(Math.random() * 400000),
      type: "day" as const,
      ...pointRange(d, d),
    };
  });
};

/**
 * Genera datos mensuales para un año
 */
export const generateMonthlyData = (
  yearDateInput: DateInput,
): ChartDataPoint[] => {
  const yearDate = safeDate(yearDateInput);

  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(yearDate.getFullYear(), i, 1);
    return {
      name: format(d, "MMM", { locale: es }),
      fullName: `${cap(format(d, "MMMM", { locale: es }))} ${d.getFullYear()}`,
      fullDate: d,
      total: 15000000 + Math.floor(Math.random() * 5000000),
      type: "month",
      ...pointRange(d, new Date(d.getFullYear(), i + 1, 0)),
    };
  });
};

/* ── Generadores de gastos de caja ── */

/**
 * Genera datos de gastos y retiros por hora para un día específico (24 horas).
 * Los gastos se concentran en el horario operativo del día; de madrugada son mínimos.
 */
export const generateHourlyExpenseData = (
  dateInput: DateInput,
): ChartDataPoint[] => {
  const date = safeDate(dateInput);

  return ALL_DAY_HOURS.map((hour) => {
    const isNight = hour <= 6 || hour >= 22;
    const hourDate = new Date(date);
    hourDate.setHours(hour, 0, 0, 0);
    return {
      name: format(hourDate, "h a", { locale: es })
        .replace("a. m.", "am")
        .replace("p. m.", "pm"),
      fullDate: hourDate,
      total: isNight
        ? 2000 + Math.floor(Math.random() * 8000)
        : 10000 + Math.floor(Math.random() * 40000),
      type: "hour",
      ...pointRange(date, date),
    };
  });
};

/**
 * Genera datos de gastos y retiros diarios para una semana
 */
export const generateDailyExpenseData = (
  startDateInput: DateInput,
): ChartDataPoint[] => {
  const startDate = safeDate(startDateInput);
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });

  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i);
    return {
      name: format(d, "EEEE", { locale: es }),
      shortName: format(d, "EEE", { locale: es }),
      fullDate: d,
      total: 100000 + Math.floor(Math.random() * 300000),
      type: "day",
      ...pointRange(d, d),
    };
  });
};

/**
 * Genera datos de gastos y retiros por día para todos los días de un mes
 */
export const generateDailyExpenseDataForMonth = (
  monthDateInput: DateInput,
): ChartDataPoint[] => {
  const monthDate = safeDate(monthDateInput);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month, i + 1);
    return {
      name: String(i + 1),
      shortName: String(i + 1),
      fullName: `${d.getDate()} de ${cap(format(d, "MMMM", { locale: es }))}`,
      fullDate: d,
      total: 100000 + Math.floor(Math.random() * 300000),
      type: "day" as const,
      ...pointRange(d, d),
    };
  });
};

/**
 * Genera datos de gastos y retiros mensuales para un año
 */
export const generateMonthlyExpenseData = (
  yearDateInput: DateInput,
): ChartDataPoint[] => {
  const yearDate = safeDate(yearDateInput);

  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(yearDate.getFullYear(), i, 1);
    return {
      name: format(d, "MMM", { locale: es }),
      fullName: `${cap(format(d, "MMMM", { locale: es }))} ${d.getFullYear()}`,
      fullDate: d,
      total: 3000000 + Math.floor(Math.random() * 4000000),
      type: "month",
      ...pointRange(d, new Date(d.getFullYear(), i + 1, 0)),
    };
  });
};

/* ── Generadores de ventas tienda virtual ────────────────────────────────────
 * Representan pré pedidos entregados/confirmados via canal web o whatsapp.
 * Se usan como serie independiente en gráficos de métricas. */

/** Ventas de tienda virtual por hora (un día, 24 horas) */
export const generateHourlyTiendaData = (
  dateInput: DateInput,
): ChartDataPoint[] => {
  const date = safeDate(dateInput);
  return ALL_DAY_HOURS.map((hour) => {
    const isPeak = (hour >= 10 && hour <= 11) || (hour >= 19 && hour <= 21);
    const isNight = hour <= 5 || hour === 23;
    const base = isPeak ? 50000 : isNight ? 8000 : 25000;
    const value = base + Math.floor(Math.random() * (isNight ? 12000 : 25000));
    const hourDate = new Date(date);
    hourDate.setHours(hour, 0, 0, 0);
    return {
      name: format(hourDate, "h a", { locale: es })
        .replace("a. m.", "am")
        .replace("p. m.", "pm"),
      fullDate: hourDate,
      total: value,
      type: "hour",
      ...pointRange(date, date),
    };
  });
};

/** Ventas de tienda virtual por día (una semana) */
export const generateDailyTiendaData = (
  startDateInput: DateInput,
): ChartDataPoint[] => {
  const startDate = safeDate(startDateInput);
  const weekStart = startOfWeek(startDate, { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i);
    return {
      name: format(d, "EEEE", { locale: es }),
      shortName: format(d, "EEE", { locale: es }),
      fullDate: d,
      total: 150000 + Math.floor(Math.random() * 200000),
      type: "day",
      ...pointRange(d, d),
    };
  });
};

/** Ventas de tienda virtual por día (todos los días de un mes) */
export const generateDailyTiendaDataForMonth = (
  monthDateInput: DateInput,
): ChartDataPoint[] => {
  const monthDate = safeDate(monthDateInput);
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month, i + 1);
    return {
      name: String(i + 1),
      shortName: String(i + 1),
      fullName: `${d.getDate()} de ${cap(format(d, "MMMM", { locale: es }))}`,
      fullDate: d,
      total: 150000 + Math.floor(Math.random() * 200000),
      type: "day" as const,
      ...pointRange(d, d),
    };
  });
};

/** Ventas de tienda virtual por mes (un año completo) */
export const generateMonthlyTiendaData = (
  yearDateInput: DateInput,
): ChartDataPoint[] => {
  const yearDate = safeDate(yearDateInput);
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(yearDate.getFullYear(), i, 1);
    return {
      name: format(d, "MMM", { locale: es }),
      fullName: `${cap(format(d, "MMMM", { locale: es }))} ${d.getFullYear()}`,
      fullDate: d,
      total: 3000000 + Math.floor(Math.random() * 3000000),
      type: "month",
      ...pointRange(d, new Date(d.getFullYear(), i + 1, 0)),
    };
  });
};

/* ── Generadores de "horas pico" ─────────────────────────────────────────────
 * Permiten visualizar a qué hora del día se vende más, comparando ventas de
 * caja (POS físico) y tienda virtual (web/whatsapp). El valor representado es
 * la suma agregada para cada hora dentro del periodo seleccionado:
 *   - "hoy"    → ventas reales por hora del día actual
 *   - "semana" → suma por hora de los últimos 7 días
 *   - "mes"    → suma por hora del último mes
 *   - "anio"   → suma por hora del último año
 *
 * Devuelve un dataset orientado a hora del día, NO a fecha calendaria, por lo
 * que el eje X siempre cubre las 24 horas del día (00:00 – 23:00). */

/** Periodo soportado por el gráfico de horas pico */
export type PeakHoursPeriod = "hoy" | "semana" | "mes" | "anio";

/** Punto del dataset de horas pico — orientado a hora, no a fecha */
export interface PeakHourDataPoint {
  /** Hora numérica 0–23 (clave estable) */
  hour: number;
  /** Etiqueta corta para eje X (e.g. "8am", "1pm") */
  name: string;
  /** Etiqueta extendida para tooltip (e.g. "8:00 a. m.") */
  fullName: string;
  /** Ventas agregadas en esta hora — Caja (POS físico) */
  caja: number;
  /** Ventas agregadas en esta hora — Tienda Virtual (web/whatsapp) */
  tienda: number;
  /** Suma de ambos canales — útil para tooltip y resaltado de pico */
  total: number;
}

/** Las 24 horas del día — el negocio puede vender a cualquier hora. */
const PEAK_BUSINESS_HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Multiplicador de agregación según periodo (días aproximados de muestra) */
const PEAK_PERIOD_MULTIPLIER: Record<PeakHoursPeriod, number> = {
  hoy: 1,
  semana: 7,
  mes: 30,
  anio: 365,
};

/**
 * Genera el dataset de horas pico agregado por canal (Caja vs Tienda Virtual)
 * para el periodo solicitado. Datos simulados — desacoplados del backend.
 */
export const generatePeakHoursData = (
  period: PeakHoursPeriod,
): PeakHourDataPoint[] => {
  const multiplier = PEAK_PERIOD_MULTIPLIER[period];

  return PEAK_BUSINESS_HOURS.map((hour) => {
    const refDate = new Date();
    refDate.setHours(hour, 0, 0, 0);

    // Caja: pico al mediodía y cena (12–15h, 18–21h); madrugada baja (0–6h)
    const cajaPeak =
      hour >= 12 && hour <= 15
        ? 1.8
        : hour >= 18 && hour <= 21
          ? 1.6
          : hour <= 6
            ? 0.25
            : 1;
    const cajaBase = 60000 * cajaPeak;
    const caja = Math.floor(
      (cajaBase + Math.random() * 40000 * cajaPeak) * multiplier,
    );

    // Tienda virtual: pico en mañana y noche (10–11h, 19–21h); madrugada baja (0–5h)
    const tiendaPeak =
      hour >= 10 && hour <= 11
        ? 2
        : hour >= 19 && hour <= 21
          ? 2
          : hour <= 5
            ? 0.4
            : 1;
    const tiendaBase = 25000 * tiendaPeak;
    const tienda = Math.floor(
      (tiendaBase + Math.random() * 20000 * tiendaPeak) * multiplier,
    );

    return {
      hour,
      name: format(refDate, "h a", { locale: es })
        .replace("a. m.", "am")
        .replace("p. m.", "pm"),
      fullName: format(refDate, "h:mm a", { locale: es })
        .replace("a. m.", "a.m.")
        .replace("p. m.", "p.m."),
      caja,
      tienda,
      total: caja + tienda,
    };
  });
};

/* ── Métodos de pago más usados ─────────────────────────────────────────── */

/**
 * Dataset del gráfico "Métodos de Pago Más Usados".
 *
 * El eje X es TIEMPO (meses, días u horas según el nivel), y cada método de
 * pago es una serie. Eso permite tanto el ranking (método con mayor monto,
 * resumido en el subtítulo) como el drill-down (clic → Año→Mes→Día→Horas),
 * igual que Tendencia de Ventas.
 */

/** Nivel base (menú de periodos) del gráfico de métodos de pago */
export type PaymentMethodsPeriod = "hoy" | "semana" | "mes" | "anio";

/** Nivel de agregación visible (incluye el drill interno "dia") */
export type PaymentMethodsLevel =
  | PaymentMethodsPeriod
  | "dia"
  | "quincena"
  | "quincena_dias";

/** Ranking de métodos por monto total (para exportación/resumen) */
export interface PaymentMethodDataPoint {
  /** ID estable del método */
  id: string;
  /** Etiqueta del método */
  name: string;
  /** Etiqueta extendida (hoy igual que name) */
  fullName: string;
  /** Monto total vendido con este método en el periodo visible */
  monto: number;
}

/**
 * Punto de la serie temporal de métodos de pago: cada punto es un bucket de
 * tiempo (hora, día o mes) y cada método es una clave numérica del punto.
 */
export interface PaymentMethodSeriesPoint {
  /** Clave estable del bucket (hora, ISO day o índice de mes) */
  key: string;
  /** Etiqueta corta para eje X */
  name: string;
  /** Etiqueta extendida para tooltip */
  fullName: string;
  /** Fecha ISO (YYYY-MM-DD) del bucket — destino del drill al hacer clic */
  dateIso: string;
  /** Monto por id de método de pago (claves dinámicas) */
  [methodId: string]: number | string;
}

/** Método de pago presente en una serie temporal */
export interface PaymentMethodSeriesEntry {
  id: string;
  label: string;
}

/** Serie temporal completa + métodos presentes, ordenados por total desc. */
export interface PaymentMethodSeries {
  points: PaymentMethodSeriesPoint[];
  methods: PaymentMethodSeriesEntry[];
}
